/**
 * TIER 3: Session Polling Hook for Cross-Mode Logout Detection
 *
 * This hook implements 15-second polling to detect when a user's session
 * has been invalidated from another browser mode/window.
 *
 * Usage:
 * const { lastPolled, isPolling } = useSessionPoller(token, handleLogout);
 *
 * Features:
 * - Polls /api/auth/session/check every 15 seconds
 * - Detects session invalidation across browser modes
 * - Handles network errors gracefully
 * - Cleans up polling interval on unmount
 * - Only polls if token exists and user is authenticated
 */

import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../../config/apiConfig';

const POLL_INTERVAL = 15000; // 15 seconds
const INITIAL_POLL_DELAY = 1000; // 1 second delay before first poll (allow session creation to complete)
const SESSION_NOT_FOUND_RETRIES = 3; // Retry up to 3 times if session not found (race condition tolerance)

export const useSessionPoller = (token, handleLogoutRef) => {
  const [lastPolled, setLastPolled] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const pollIntervalRef = useRef(null);
  const isFirstPollRef = useRef(true);
  const sessionNotFoundCountRef = useRef(0); // Track session_not_found retries

  useEffect(() => {
    // Only set up polling if token exists
    if (!token) {
      // Clear polling if logged out
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      setIsPolling(false);
      return;
    }

    // Define the polling function
    const pollSession = async () => {
      try {
        setIsPolling(true);

        const response = await axios.post(
          API_ENDPOINTS.AUTH.SESSION_CHECK,
          {},
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );

        const data = response.data;
        setLastPolled(new Date().toISOString());

        // Check if session is still active
        if (data.is_active === false) {
          console.warn(
            '[SESSION_CHECK] Session check failed. Reason:',
            data.reason
          );

          // CRITICAL FIX: Handle session_not_found with retry logic
          // This can happen due to race condition between login response and session insert
          // After N retries, force logout (don't wait for interceptor)
          if (data.reason === 'session_not_found') {
            sessionNotFoundCountRef.current += 1;

            // Only logout after N retries without finding session
            // This gives database time to sync the session record
            if (sessionNotFoundCountRef.current < SESSION_NOT_FOUND_RETRIES) {
              console.warn(
                `[SESSION_CHECK] Session not found - retry ${sessionNotFoundCountRef.current}/${SESSION_NOT_FOUND_RETRIES}. ` +
                `Possible cause: database sync delay after login`
              );
              // Don't logout yet - return to try again on next poll
              return;
            }

            // If we've exceeded retries, force logout now (don't continue)
            console.error('[SESSION_CHECK] Session not found after retries - forcing logout');
            if (handleLogoutRef?.current && typeof handleLogoutRef.current === 'function') {
              handleLogoutRef.current('device', 'session_expired');
            }
            return;  // ← CRITICAL: Exit here to prevent falling through to other logic
          } else {
            // For other reasons (session_closed, etc.), reset retry counter
            sessionNotFoundCountRef.current = 0;
          }

          // Call logout handler via ref (TIER 3: Avoid dependency issues)
          // CRITICAL: Check if handleLogoutRef.current is not null
          // It's set to null when logout is already in progress (prevents infinite loop in incognito)
          if (handleLogoutRef?.current && typeof handleLogoutRef.current === 'function') {
            // Pass reason for kicked-out page display
            // Map server reasons to frontend kickout reasons
            let logoutReason = 'session_expired'; // Default
            if (data.reason === 'session_invalidated') {
              logoutReason = 'logged_out_other_device';
            } else if (data.reason === 'session_closed') {
              // Browser refresh or session was closed
              logoutReason = 'session_closed';
            }
            // NOTE: location_disabled is now handled by axios interceptor (403 with force_logout)
            // It won't reach here because decorator/interceptor catches it first
            handleLogoutRef.current('device', logoutReason);
          } else {
            console.warn('handleLogoutRef.current is null or not a function - logout not triggered');
          }
        } else if (data.is_active === true) {
          // Session is active - reset retry counter on success
          sessionNotFoundCountRef.current = 0;

          // Log success on first poll only
          if (isFirstPollRef.current) {
            isFirstPollRef.current = false;
          }
        }
      } catch (error) {
        // Handle errors from session check endpoint
        // NOTE: 403 with location_disabled is now handled by axios interceptor
        // NOTE: 401 auth errors are handled by axios interceptor

        if (error.response?.status === 403) {
          // 403 errors (like location_disabled) are caught by axios interceptor
          // which triggers immediate logout via force_logout flag
          console.error('403 error - axios interceptor should handle this:', error.response.data);
        } else if (error.response?.status === 401) {
          // 401 errors are caught by axios interceptor
          console.error('401 error - axios interceptor should handle this');
        } else if (error.code === 'ECONNABORTED' || error.code === 'ENOTFOUND') {
          // Network error - log but don't logout
          console.warn('Network error during session check:', error.message);
        } else if (error.response) {
          // Other HTTP errors
          console.warn('Error checking session:', error.response.status, error.message);
        } else {
          // No response (timeout, connection refused, etc.)
          console.warn('Error checking session (no response):', error.message);
        }
      } finally {
        setIsPolling(false);
      }
    };

    // Start polling immediately (don't wait 15 seconds for first check)
    pollSession();

    // Set up interval for subsequent polls
    pollIntervalRef.current = setInterval(pollSession, POLL_INTERVAL);

    // Cleanup on unmount or when token changes
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [token]);

  return {
    lastPolled,
    isPolling
  };
};

export default useSessionPoller;
